import { toast } from "sonner";
import { Check, PencilLine } from "lucide-react";

export function truncateFact(s, n = 90) {
  const t = (s || "").trim();
  return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t;
}

function isCorrection(r) {
  return Boolean(r && (r.superseded || r.memory_type === "correction"));
}

// Pure — builds the confirmation copy from the already-persisted structured result.
export function buildRememberedTitle(remembered) {
  if (!Array.isArray(remembered) || remembered.length === 0) return null;
  const count = remembered.length;
  if (count === 1) {
    const r = remembered[0];
    return isCorrection(r)
      ? `Sarge updated what he knows: ${truncateFact(r.statement)}`
      : `Sarge remembered: ${truncateFact(r.statement)}`;
  }
  const verb = remembered.every(isCorrection) ? "updated" : "remembered";
  return `Sarge ${verb} ${count} things`;
}

// Subtle, non-blocking confirmation shown ONLY after a successful persist.
// No approval step; auto-dismisses after 5s; View/Correct link to "What Sarge Knows".
export function notifyRemembered(remembered, navigate) {
  const title = buildRememberedTitle(remembered);
  if (!title) return;
  const anyCorrection = remembered.some(isCorrection);
  // Only deep-link into a specific editor when a single fact was captured.
  const correctHref = remembered.length === 1 ? `/app/memory?correct=${remembered[0].id}` : "/app/memory";

  toast.custom(
    (t) => (
      <div
        data-testid="memory-confirm-toast"
        role="status"
        aria-live="polite"
        className="w-[340px] max-w-[calc(100vw-2rem)] border border-wr-border border-l-2 border-l-emerald-500 bg-wr-surface px-4 py-3 shadow-xl"
      >
        <div className="flex items-start gap-2">
          <span className="text-emerald-400 mt-0.5 shrink-0" aria-hidden="true">
            {anyCorrection ? <PencilLine size={15} /> : <Check size={15} />}
          </span>
          <p className="text-sm text-white leading-snug flex-1 break-words" data-testid="memory-confirm-text">
            {title}
          </p>
        </div>
        <div className="flex items-center gap-2 mt-2.5 pl-6">
          <button
            type="button"
            data-testid="memory-confirm-view"
            onClick={() => {
              toast.dismiss(t);
              navigate("/app/memory");
            }}
            className="wr-mono text-[0.66rem] uppercase tracking-wider text-wr-sub hover:text-white border border-wr-border px-2.5 py-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500"
          >
            View
          </button>
          <button
            type="button"
            data-testid="memory-confirm-correct"
            onClick={() => {
              toast.dismiss(t);
              navigate(correctHref);
            }}
            className="wr-mono text-[0.66rem] uppercase tracking-wider text-wr-sub hover:text-white border border-wr-border px-2.5 py-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500"
          >
            Correct
          </button>
        </div>
      </div>
    ),
    { duration: 5000 }
  );
}
